/**
 * Bilingual (EN + BN) error normaliser used by shared route error components.
 *
 * Recognises common Supabase / PostgREST codes, auth failures, and network
 * outages so retry screens can show something more actionable than a raw
 * `Error.message`. Anything unknown falls back to the truncated original
 * message.
 */

export type ParsedError = {
  title: string;
  titleBn: string;
  message: string;
  messageBn: string;
  /** Optional short remediation hint (EN + BN, space separated). */
  hint?: string;
  /** True if the caller should suggest re-login. */
  requiresReauth?: boolean;
};

type Anyish = Record<string, unknown> | null | undefined;

function get(obj: Anyish, key: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  return (obj as Record<string, unknown>)[key];
}

function truncate(v: unknown, max = 220): string {
  const s = typeof v === "string" ? v : v == null ? "" : String(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export function parseVendorError(err: unknown): ParsedError {
  const anyErr = err as Anyish;
  const rawMessage = truncate(get(anyErr, "message") ?? err);
  const code = get(anyErr, "code");
  const status = get(anyErr, "status") ?? get(anyErr, "statusCode");
  const name = String(get(anyErr, "name") ?? "");

  // Network / offline
  if (
    (typeof navigator !== "undefined" && navigator.onLine === false) ||
    (name === "TypeError" && /fetch/i.test(rawMessage)) ||
    /network|failed to fetch|load failed/i.test(rawMessage)
  ) {
    return {
      title: "You appear to be offline",
      titleBn: "আপনি অফলাইনে আছেন",
      message: "We couldn't reach the server. Check your connection and try again.",
      messageBn: "সার্ভারে পৌঁছানো যায়নি। ইন্টারনেট সংযোগ দেখে আবার চেষ্টা করুন।",
    };
  }

  // Auth
  if (status === 401 || /jwt|unauthori[sz]ed|invalid token|no authorization/i.test(rawMessage)) {
    return {
      title: "Your session expired",
      titleBn: "আপনার সেশন শেষ হয়েছে",
      message: "Please sign in again to continue.",
      messageBn: "চালিয়ে যেতে আবার লগ ইন করুন।",
      hint: "Sign in · আবার লগ ইন",
      requiresReauth: true,
    };
  }

  if (status === 403 || code === "42501" || /permission denied|forbidden|rls/i.test(rawMessage)) {
    return {
      title: "You don't have access to this",
      titleBn: "এই তথ্য দেখার অনুমতি নেই",
      message: "This account can't view or change these records.",
      messageBn: "এই অ্যাকাউন্ট থেকে এই রেকর্ডগুলো দেখা বা পরিবর্তন করা যাবে না।",
    };
  }

  if (code === "PGRST116" || status === 404 || /not found/i.test(rawMessage)) {
    return {
      title: "We couldn't find that record",
      titleBn: "রেকর্ডটি পাওয়া যায়নি",
      message: "It may have been removed. Refresh the list and try again.",
      messageBn: "রেকর্ডটি হয়তো মুছে ফেলা হয়েছে। তালিকা রিফ্রেশ করে আবার চেষ্টা করুন।",
    };
  }

  if (code === "23505") {
    return {
      title: "Duplicate entry",
      titleBn: "ডুপ্লিকেট এন্ট্রি",
      message: "A record with the same key already exists.",
      messageBn: "একই তথ্যের একটি রেকর্ড ইতিমধ্যে রয়েছে।",
    };
  }

  if (code === "23503") {
    return {
      title: "Linked record required",
      titleBn: "সংযুক্ত রেকর্ড প্রয়োজন",
      message: "This item depends on data that no longer exists.",
      messageBn: "এই আইটেমটি এমন তথ্যের উপর নির্ভরশীল যা আর উপলব্ধ নেই।",
    };
  }

  if (status === 429 || /rate.?limit|too many/i.test(rawMessage)) {
    return {
      title: "You're going a little too fast",
      titleBn: "একটু ধীরে চেষ্টা করুন",
      message: "Please wait a moment and try again.",
      messageBn: "একটু অপেক্ষা করে আবার চেষ্টা করুন।",
    };
  }

  if (typeof status === "number" && status >= 500) {
    return {
      title: "The server had a problem",
      titleBn: "সার্ভারে সমস্যা হয়েছে",
      message: "This is on our side. Please retry in a moment.",
      messageBn: "সমস্যাটি আমাদের দিকে। কিছুক্ষণ পরে আবার চেষ্টা করুন।",
    };
  }

  return {
    title: "Something went wrong",
    titleBn: "কিছু একটা সমস্যা হয়েছে",
    message: rawMessage || "This page couldn't load.",
    messageBn: "এই পেজটি লোড করা যায়নি।",
  };
}
